import { forwardRef } from 'react';
import { SourceLabel, STATUS_BADGE } from '../../constants/navios';
import { FormatDateShort } from '../../utils/date';
import { FormatBrazilDate } from '../../utils/dateUtils';
import { FormatDecimal1, FormatDurationHuman } from '../../utils/format';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { Mono } from '../ui/Mono';

const formatDayTime = (iso) =>
    iso ? FormatBrazilDate(iso, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const Field = ({ label, children }) => (
    <div className="min-w-0">
        <dt className="text-[11px] uppercase tracking-wide text-mono-500 dark:text-mono-400">{label}</dt>
        <dd className="mt-0.5 text-sm text-black dark:text-white">{children}</dd>
    </div>
);

/**
 * Um navio: nome + tags, onde está + status, velocidade, destino/ETA do AIS,
 * ETA informada, última leitura e os avisos. No celular é a unidade da lista.
 */
const NavioCard = forwardRef(
    ({ vessel, color, selected = false, canWrite = false, now, onSelect, onEdit, onPosition, onArchive }, ref) => {
        const last = vessel.last_position;
        const minutesAgo = last ? (now - new Date(last.observed_at).getTime()) / 60_000 : null;
        return (
            <article
                ref={ref}
                className={`rounded-xl border bg-white p-4 transition-shadow dark:bg-mono-950 ${
                    selected
                        ? 'border-black shadow-md ring-1 ring-black dark:border-white dark:ring-white'
                        : 'border-mono-200 dark:border-mono-800'
                }`}
            >
                <button type="button" className="block w-full text-left" onClick={() => onSelect?.(vessel.id)}>
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <h3 className="flex items-center gap-2 text-base font-semibold text-black dark:text-white">
                                <span
                                    className="inline-block h-3 w-3 shrink-0 rounded-full"
                                    style={{ backgroundColor: color }}
                                    aria-hidden="true"
                                />
                                <span className="truncate">{vessel.name}</span>
                            </h3>
                            <p className="mt-0.5 text-xs text-mono-500 dark:text-mono-400">
                                IMO <Mono>{vessel.imo}</Mono>
                                {vessel.mmsi ? (
                                    <>
                                        {' · '}MMSI <Mono>{vessel.mmsi}</Mono>
                                    </>
                                ) : null}
                            </p>
                        </div>
                        <Badge variant={STATUS_BADGE[vessel.status] || 'default'} className="shrink-0">
                            {vessel.status_label}
                        </Badge>
                    </div>

                    {vessel.tags.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {vessel.tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="rounded-full border border-mono-200 px-2 py-0.5 text-xs text-mono-700 dark:border-mono-700 dark:text-mono-300"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    ) : null}

                    <p className="mt-3 text-sm font-medium text-black dark:text-white">
                        {vessel.onde_esta?.frase || 'Sem posição ainda'}
                    </p>
                </button>

                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
                    <Field label="Velocidade">
                        {last?.speed_kn !== null && last?.speed_kn !== undefined ? (
                            <>
                                <Mono>{FormatDecimal1(last.speed_kn)}</Mono> kn
                            </>
                        ) : vessel.onde_esta?.velocidade_fonte === 'derivada' ? (
                            <>
                                ~<Mono>{FormatDecimal1(vessel.onde_esta.velocidade_usada_kn)}</Mono> kn (calculada)
                            </>
                        ) : (
                            '—'
                        )}
                    </Field>
                    <Field label="Destino (AIS)">
                        <span className="break-words">{last?.destination || '—'}</span>
                    </Field>
                    <Field label="ETA do AIS">{last?.eta_ais ? formatDayTime(last.eta_ais) : '—'}</Field>
                    <Field label="ETA informada">
                        {vessel.eta_informed ? FormatDateShort(vessel.eta_informed) : '—'}
                        {vessel.eta_informed_note ? (
                            <span className="block text-xs text-mono-500 dark:text-mono-400">
                                {vessel.eta_informed_note}
                            </span>
                        ) : null}
                    </Field>
                    <Field label="Última leitura">
                        {last ? (
                            <>
                                {formatDayTime(last.observed_at)}
                                <span className="block text-xs text-mono-500 dark:text-mono-400">
                                    há {FormatDurationHuman(minutesAgo)} ·{' '}
                                    {SourceLabel(last.source, last.reported_source)}
                                </span>
                            </>
                        ) : (
                            '—'
                        )}
                    </Field>
                    <Field label="Chegada estimada">
                        {vessel.onde_esta?.eta_estimada ? formatDayTime(vessel.onde_esta.eta_estimada) : '—'}
                    </Field>
                </dl>

                {vessel.avisos.length > 0 || vessel.last_provider_error ? (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {vessel.avisos.map((aviso) => (
                            <Badge key={aviso.code} variant="warning">
                                {aviso.message}
                            </Badge>
                        ))}
                        {vessel.last_provider_error ? (
                            <Badge variant="danger">Provider: {vessel.last_provider_error}</Badge>
                        ) : null}
                    </div>
                ) : null}

                {vessel.note ? (
                    <p className="mt-3 whitespace-pre-line text-xs text-mono-600 dark:text-mono-400">{vessel.note}</p>
                ) : null}

                {canWrite ? (
                    <div className="mt-3 flex flex-wrap gap-2 border-t border-mono-100 pt-3 dark:border-mono-900">
                        <Button size="sm" variant="secondary" onClick={() => onEdit?.(vessel)}>
                            Editar tags/ETA
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onPosition?.(vessel)}>
                            Registrar posição
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onArchive?.(vessel)}>
                            Arquivar
                        </Button>
                    </div>
                ) : null}
            </article>
        );
    },
);

NavioCard.displayName = 'NavioCard';

export default NavioCard;
